import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Mail, 
  MapPin, 
  Facebook, 
  Linkedin, 
  Instagram,
  ArrowRight
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

// X (formerly Twitter) logo component
const XLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f7db9eb5-303b-40e1-ba38-b76b077cd7ba/artifacts/fty4yugx_Utility%20logo_page-0001%20%281%29.jpg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const footerLinks = {
  services: [
    { name: 'Utility Locating & Marking', path: '/services#locating' },
    { name: 'Damage Prevention Programs', path: '/services#damage-prevention' },
    { name: 'Field Intelligence & Reporting', path: '/services#field-intelligence' },
    { name: 'Emergency Response', path: '/services#emergency' },
  ],
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Industries', path: '/industries' },
    { name: 'Locations', path: '/locations' },
    { name: 'Careers', path: '/careers' },
    { name: 'Safety Center', path: '/safety' },
    { name: 'Contact Us', path: '/contact' },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already subscribed');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { theme } = useTheme();

  return (
    <footer className={`border-t border-skytec-border ${theme === 'dark' ? 'bg-[#0a1628]' : 'bg-skytec-primary'}`}>
      {/* Newsletter Section */}
      <div className="border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2">
                STAY INFORMED ON SAFETY
              </h3>
              <p className="text-white/70">
                Get the latest safety tips and industry news delivered to your inbox.
              </p>
            </div>
            <form onSubmit={handleNewsletter} className="flex w-full lg:w-auto gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/30 text-white placeholder:text-white/50 w-full lg:w-80"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-semibold whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                SUBSCRIBE
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" data-testid="footer-logo">
              <img src={LOGO_URL} alt="USG Logo" className="h-36 w-auto" />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed">
              Precision. Protection. Progress. Protecting underground infrastructure 
              with advanced technology. A Skytec Company.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-white/60 hover:text-skytec-secondary transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-white/60 hover:text-skytec-secondary transition-colors" data-testid="social-x">
                <XLogo className="w-5 h-5" />
              </a>
              <a href="#" className="text-white/60 hover:text-skytec-secondary transition-colors" data-testid="social-linkedin">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="text-white/60 hover:text-skytec-secondary transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="font-heading font-bold text-white mb-6 tracking-wider">SERVICES</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/70 hover:text-skytec-secondary transition-colors text-sm flex items-center group"
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="font-heading font-bold text-white mb-6 tracking-wider">COMPANY</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/70 hover:text-skytec-secondary transition-colors text-sm flex items-center group"
                    data-testid={`footer-company-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-heading font-bold text-white mb-6 tracking-wider">CONTACT</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="mailto:info@usgllc811.com" 
                  className="text-white/70 hover:text-skytec-secondary transition-colors text-sm flex items-center"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 mr-3 text-skytec-secondary" />
                  info@usgllc811.com
                </a>
              </li>
              <li className="text-white/70 text-sm flex items-start">
                <MapPin className="w-4 h-4 mr-3 mt-0.5 text-skytec-secondary flex-shrink-0" />
                <div>
                  5092 Southwest 73 Avenue<br />
                  Davie, FL 33314<br />
                  United States
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} Utility Safe Guarding (USG). A Skytec Company. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <a href="#" className="text-white/50 hover:text-skytec-secondary transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-white/50 hover:text-skytec-secondary transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
